import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { Box, Stack, Tabs, Tab } from '@mui/material';
import { colors, IconButton, Tooltip } from '@sweep-io/sweep-design';
import { PAGE_SIDE_PADDING } from '../../../constants';
import { useExpandedMode } from '../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { TabLabel } from './TabLabel';
import { AddMetadata } from './AddMetadata';
import { useDocumentationTabs } from './useDocumentationTabs';
import { useSelector, useDispatch } from 'react-redux';
import {
  DEFAULT_OBJECTS_TAB_ID,
  selectActiveDocumentationPageTab,
  selectPageTabs,
  setDocumentationPageType,
} from '../../../reducers/documentation/documentationPageReducer';
import TagIcon from './Tag.svg?react';
import { TabListItem } from './TabListItem';
import { DocumentationPageType } from '../types';
import { getMetadataLabel } from './getMetadataLabel';
import { Close } from '@sweep-io/sweep-design/dist/icons/Close';
import { useDependencies } from '../dependencies/hooks/useDependencies';
import { useRunOnce } from '../../common/useRunOnce';

interface DocumentationTabsProps {
  children: React.ReactNode;
  documentationPageType: DocumentationPageType;
  crmOrgId: string;
}

export const DocumentationTabs = ({
  children,
  documentationPageType,
  crmOrgId,
}: DocumentationTabsProps) => {
  const pageTabs = useSelector(selectPageTabs);
  const tabs = useMemo(() => {
    return pageTabs?.[documentationPageType] ?? [];
  }, [pageTabs, documentationPageType]);

  const activeDocumentationPageTab = useSelector(selectActiveDocumentationPageTab);
  const { isExpandedMode } = useExpandedMode();
  const { onAddMetadata, onRemoveMetadata, setActiveTab } = useDocumentationTabs(crmOrgId);
  const { handleCloseDependencies } = useDependencies(crmOrgId);
  const selectedTypes = tabs.map((tab) => tab.id);
  const dispatch = useDispatch();
  const [isCompactMode, setIsCompactMode] = useState(false);

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const addMetadataRef = useRef<HTMLDivElement>(null);
  const tagIconRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    //if expanded mode and no active tab, set default tab
    if (isExpandedMode && !activeDocumentationPageTab) {
      setActiveTab(DEFAULT_OBJECTS_TAB_ID);
    }
  }, [activeDocumentationPageTab, setActiveTab, isExpandedMode]);

  useEffect(() => {
    const container = tabsContainerRef.current;
    const addMetadata = addMetadataRef.current;
    const tagIcon = tagIconRef.current;

    if (!container || !addMetadata || !tagIcon) return;

    const checkSpacing = () => {
      const addMetadataRect = addMetadata.getBoundingClientRect();
      const tagIconRect = tagIcon.getBoundingClientRect();

      // Calculate the distance between the right edge of AddMetadata and left edge of tag icon
      const distance = tagIconRect.left - addMetadataRect.right;

      // Switch to compact mode when they're less than 100px apart
      setIsCompactMode(distance < 100);
    };

    const resizeObserver = new ResizeObserver(checkSpacing);
    resizeObserver.observe(container);

    // Also check on mount
    checkSpacing();

    return () => resizeObserver.disconnect();
  }, [tabs]);

  useRunOnce(() => {
    handleCloseDependencies();
  });

  const handleTabChange = useCallback(
    (newValue: string) => {
      onAddMetadata(newValue, true);
      setActiveTab(newValue);
      handleCloseDependencies();
    },
    [onAddMetadata, setActiveTab, handleCloseDependencies],
  );

  const renderTagIcon = () => {
    if (documentationPageType === DocumentationPageType.metadataTypes) {
      return (
        <Box sx={{ pr: 0.5 }} data-testid="tags-button-icon">
          <Tooltip title="Custom tags" placement="top">
            <span>
              <IconButton
                variant="system"
                size="small"
                onClick={() => {
                  dispatch(
                    setDocumentationPageType({
                      documentationPageType: DocumentationPageType.tagsAndUsers,
                    }),
                  );
                  if (isExpandedMode) {
                    setActiveTab('tags');
                  }
                }}
              >
                <TagIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    }

    if (documentationPageType === DocumentationPageType.tagsAndUsers) {
      return (
        <Box sx={{ pr: 0.5 }}>
          <IconButton
            variant="flat"
            size="small"
            onClick={() => {
              dispatch(
                setDocumentationPageType({
                  documentationPageType: DocumentationPageType.metadataTypes,
                }),
              );
              setActiveTab(DEFAULT_OBJECTS_TAB_ID);
            }}
          >
            <Close />
          </IconButton>
        </Box>
      );
    }

    return null;
  };

  if (!isExpandedMode) {
    if (activeDocumentationPageTab) {
      return children;
    }

    return (
      <Stack
        sx={{
          height: '100%',
          overflow: 'hidden',
          mr: isExpandedMode ? PAGE_SIDE_PADDING + 'px' : 0,
          ml: isExpandedMode ? PAGE_SIDE_PADDING + 'px' : 0,
        }}
        data-testid="documentation-tabs"
      >
        {tabs.map((tab) => (
          <TabListItem
            key={tab.id}
            tab={tab}
            handleTabChange={handleTabChange}
            onRemoveMetadata={onRemoveMetadata}
          />
        ))}
        {documentationPageType === DocumentationPageType.metadataTypes && (
          <Stack
            sx={{
              flexDirection: 'row',
              position: 'sticky',
              bottom: 0,
              zIndex: 900,
              backgroundColor: colors.white,
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 2,
            }}
          >
            <AddMetadata
              onAddMetadata={onAddMetadata}
              selectedTypes={selectedTypes}
              crmOrgId={crmOrgId}
            />
            {renderTagIcon()}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        height: '100%',
        overflow: 'hidden',
        mr: isExpandedMode ? PAGE_SIDE_PADDING + 'px' : 0,
        ml: isExpandedMode ? PAGE_SIDE_PADDING + 'px' : 0,
      }}
    >
      <Stack
        ref={tabsContainerRef}
        direction="row"
        alignItems="center"
        sx={{
          backgroundColor: colors.grey[100],
          borderRadius: '8px 8px 0 0',
          position: 'relative',
          zIndex: 1,
          '&::after': {
            //fake border bottom for tabs
            content: '""',
            position: 'absolute',
            bottom: '0',
            left: '0',
            right: '0',
            height: '1px',
            backgroundColor: colors.grey[120],
            zIndex: 0,
          },
        }}
      >
        <Tabs
          value={activeDocumentationPageTab || tabs[0]?.id || DEFAULT_OBJECTS_TAB_ID}
          onChange={(_, newValue) => handleTabChange(newValue)}
          variant="standard"
          sx={{
            minHeight: '42px',
            width: 'fit-content',
            maxWidth: `calc(100% - ${isCompactMode ? '30px' : '180px'})`,
            '& .MuiTabs-flexContainer': {
              gap: 0,
              justifyContent: 'flex-start',
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
            '& .MuiTabs-scroller': {
              overflow: 'visible !important',
            },
            '& .MuiTab-root': {
              color: colors.grey[700],
              padding: '4px ',
              minWidth: 0,
              width: '185px',
              flexShrink: 1,
              overflow: 'visible',
              textAlign: 'left',
              border: `1px solid transparent`,
              borderBottom: `1px solid ${colors.grey[120]}`,
              position: 'relative',
              '& .MuiTab-wrapper': {
                width: '100%',
                overflow: 'visible',
                display: 'block',
              },
              '&.Mui-selected': {
                cursor: 'default',
                color: colors.blue[500],
                backgroundColor: colors.white,
                overflow: 'visible',
                border: `1px solid ${colors.grey[120]}`,
                borderBottom: 'none',
                borderRadius: '8px 8px 0 0',
                zIndex: 2,
                '& .tab-content': {
                  '&:after': {
                    backgroundColor: 'transparent',
                  },
                },

                '& .tab-content:hover': {
                  background: colors.white,
                },
              },
              '&:hover': {
                color: colors.grey[900],
                backgroundColor: colors.grey[50],
                '& .remove-metadata-button': {
                  display: 'inline-flex',
                },
              },
            },
          }}
        >
          {tabs.map((tab, index) => {
            const isOneBeforeSelected =
              index === tabs.findIndex((t) => t.id === activeDocumentationPageTab) - 1;

            return (
              <Tab
                key={tab.id}
                label={
                  <TabLabel
                    title={getMetadataLabel(tab.label)}
                    onRemove={tab._isNotRemovable ? undefined : () => onRemoveMetadata(tab.id)}
                  >
                    {getMetadataLabel(tab.label)}
                  </TabLabel>
                }
                value={tab.id}
                sx={{
                  '& .tab-content::after': {
                    backgroundColor: isOneBeforeSelected ? 'transparent' : colors.grey[120],
                  },
                }}
              />
            );
          })}
        </Tabs>

        {documentationPageType === DocumentationPageType.metadataTypes && (
          <AddMetadata
            onAddMetadata={onAddMetadata}
            selectedTypes={selectedTypes}
            compact={isCompactMode}
            crmOrgId={crmOrgId}
            ref={addMetadataRef}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Box ref={tagIconRef}>{renderTagIcon()}</Box>
      </Stack>
      <Stack
        sx={{
          flex: 1,
          overflow: 'hidden',
          borderLeft: `1px solid ${colors.grey[120]}`,
          borderRight: `1px solid ${colors.grey[120]}`,
          borderBottom: `1px solid ${colors.grey[120]}`,
          borderRadius: '0 0 8px 8px',
          backgroundColor: colors.white,
          position: 'relative',
          width: '100%',
        }}
      >
        {children}
      </Stack>
    </Stack>
  );
};
